import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import './TimesheetPage.css';
import { toast } from '../components/Toast';

const EMPTY_FORM = {
  workDate: new Date().toISOString().split('T')[0],
  projectId: '', startTime: '', endTime: '', hoursWorked: '', description: ''
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function ViewModal({ timesheet, onClose }) {
  if (!timesheet) return null;
  return (
    <div className="ts-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ts-modal">
        <div className="ts-modal-header">
          <h3>Timesheet Detail</h3>
          <button className="ts-close" onClick={onClose}>&times;</button>
        </div>
        <div className="ts-modal-body">
          <div className="ts-detail-grid">
            <div className="ts-detail-item">
              <span className="ts-detail-label">Work Date</span>
              <span className="ts-detail-value">{fmt(timesheet.workDate)}</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Hours Worked</span>
              <span className="ts-detail-value ts-hours">{timesheet.hoursWorked}h</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Time</span>
              <span className="ts-detail-value">
                {timesheet.startTime ? `${timesheet.startTime.slice(0,5)} – ${timesheet.endTime?.slice(0,5) || '?'}` : '—'}
              </span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Project</span>
              <span className="ts-detail-value">{timesheet.projectName || '—'}</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Status</span>
              <span className="ts-detail-value">
                <span className={`ts-badge ${timesheet.approvedByName ? 'ts-badge-approved' : 'ts-badge-pending'}`}>
                  {timesheet.approvedByName ? `✓ Approved by ${timesheet.approvedByName}` : 'Pending Approval'}
                </span>
              </span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Submitted</span>
              <span className="ts-detail-value">{fmt(timesheet.createdAt)}</span>
            </div>
          </div>

          <div className="ts-detail-section">
            <span className="ts-detail-label">Description</span>
            <div className="ts-detail-desc">
              {timesheet.description || <span className="ts-muted">No description provided</span>}
            </div>
          </div>

          {timesheet.reviewComment && (
            <div className="ts-detail-section">
              <span className="ts-detail-label">Reviewer Comment</span>
              <div className="ts-detail-desc ts-comment-existing">{timesheet.reviewComment}</div>
            </div>
          )}
        </div>
        <div className="ts-modal-footer">
          <button className="ts-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function MyTimesheets() {
  const [timesheets, setTimesheets]   = useState([]);
  const [projects, setProjects]       = useState([]);
  const [projectPage, setProjectPage] = useState(0);
  const [projectTotalPages, setProjectTotalPages] = useState(1);
  const [projectTotalElements, setProjectTotalElements] = useState(0);
  const [projectSearch, setProjectSearch]     = useState('');
  const [projectLoading, setProjectLoading]   = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const projectPickerRef = useRef(null);

  const [myEmpId, setMyEmpId]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [formErrors, setFormErrors]   = useState({});
  const [selected, setSelected]       = useState(null);

  useEffect(() => { init(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target)) {
        setProjectDropdownOpen(false);
      }
    };
    if (projectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [projectDropdownOpen]);

  const fetchProjects = async (page = 0, search = '') => {
    setProjectLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', '20');
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      const res = await axios.get(`/projects?${params.toString()}`);
      const content = res.data?.data?.content || res.data?.content || [];
      const totalPages = res.data?.data?.totalPages || res.data?.totalPages || 1;
      const totalElements = res.data?.data?.totalElements || res.data?.totalElements || 0;
      setProjects(Array.isArray(content) ? content : []);
      setProjectPage(page);
      setProjectTotalPages(totalPages);
      setProjectTotalElements(totalElements);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setProjectLoading(false);
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      const profileRes = await axios.get('/employees/my-profile');
      const emp = profileRes.data?.data || profileRes.data;
      if (emp?.id) {
        setMyEmpId(emp.id);
        const res = await axios.get(`/employees/${emp.id}/timesheets`);
        const data = res.data?.timesheets || res.data?.data?.timesheets || [];
        setTimesheets(Array.isArray(data) ? data : []);
      }
      await fetchProjects(0, '');
    } catch { toast.error('Failed to load timesheets'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSelectedProject(null);
    setProjectSearch('');
    fetchProjects(0, '');
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      workDate: t.workDate || '',
      projectId: t.projectId || '',
      startTime: t.startTime ? t.startTime.slice(0, 5) : '',
      endTime:   t.endTime   ? t.endTime.slice(0, 5)   : '',
      hoursWorked: t.hoursWorked != null ? String(t.hoursWorked) : '',
      description: t.description || '',
    });
    setFormErrors({});
    if (t.projectId) {
      const existing = projects.find(p => String(p.id) === String(t.projectId));
      setSelectedProject(existing || { id: t.projectId, name: t.projectName || `Project #${t.projectId}` });
    } else {
      setSelectedProject(null);
    }
    setProjectSearch('');
    fetchProjects(0, '');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSelectedProject(null);
    setProjectDropdownOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'startTime' || name === 'endTime') {
        const start = name === 'startTime' ? value : prev.startTime;
        const end   = name === 'endTime'   ? value : prev.endTime;
        if (start && end) {
          const [sh, sm] = start.split(':').map(Number);
          const [eh, em] = end.split(':').map(Number);
          let mins = (eh * 60 + em) - (sh * 60 + sm);
          if (mins < 0) mins += 24 * 60;
          // Round to nearest quarter-hour to avoid floating-point noise (e.g. 11.98 → 12.00)
          const hours = Math.round(mins / 15) * 15 / 60;
          updated.hoursWorked = String(parseFloat(hours.toFixed(2)));
        }
      }
      return updated;
    });
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.workDate) errs.workDate = 'Work date is required';
    const h = parseFloat(form.hoursWorked);
    if (!form.hoursWorked || isNaN(h) || h <= 0) errs.hoursWorked = 'Hours worked must be greater than 0';
    else if (h > 24) errs.hoursWorked = 'Cannot exceed 24 hours in a day';
    if (form.startTime && form.endTime && form.startTime === form.endTime)
      errs.endTime = 'End time must differ from start time';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        workDate: form.workDate,
        hoursWorked: parseFloat(form.hoursWorked),
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        description: form.description,
        projectId: form.projectId || null,
      };
      if (editingId) {
        await axios.put(`/employees/${myEmpId}/timesheets/${editingId}`, payload);
        flash('Timesheet updated');
      } else {
        await axios.post(`/employees/${myEmpId}/timesheets`, payload);
        flash('Timesheet submitted for approval');
      }
      closeForm();
      init();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save timesheet');
    } finally { setSubmitting(false); }
  };

  const flash = (msg) => { toast.success(msg); };

  const totalHours = timesheets.reduce((s, t) => s + parseFloat(t.hoursWorked || 0), 0);
  const approved   = timesheets.filter(t => t.approvedByName).length;
  const pending    = timesheets.filter(t => !t.approvedByName).length;

  return (
    <div className="ts-container">
      <div className="ts-header">
        <div>
          <h1 className="ts-title">My Timesheets</h1>
          <p className="ts-subtitle">Submit and track your work hours</p>
        </div>
        <button className="ts-btn-primary" onClick={openCreate}>+ Submit Timesheet</button>
      </div>

      <div className="ts-summary">
        <div className="ts-stat"><span className="ts-stat-value">{timesheets.length}</span><span className="ts-stat-label">Total Entries</span></div>
        <div className="ts-stat"><span className="ts-stat-value">{totalHours.toFixed(1)}h</span><span className="ts-stat-label">Total Hours</span></div>
        <div className="ts-stat"><span className="ts-stat-value ts-approved">{approved}</span><span className="ts-stat-label">Approved</span></div>
        <div className="ts-stat"><span className="ts-stat-value ts-pending">{pending}</span><span className="ts-stat-label">Pending</span></div>
      </div>

      {showForm && (
        <div className="ts-form-card">
          <div className="ts-form-header">
            <h3>{editingId ? 'Edit Timesheet' : 'New Entry'}</h3>
            <button className="ts-close" onClick={closeForm}>&times;</button>
          </div>
          <form onSubmit={handleSubmit} className="ts-form">
            <div className="ts-form-row">
              <div className="ts-form-group">
                <label>Work Date *</label>
                <input type="date" name="workDate" value={form.workDate} onChange={handleChange}
                  className={formErrors.workDate ? 'error' : ''} max={new Date().toISOString().split('T')[0]} required />
                {formErrors.workDate && <span className="ts-error">{formErrors.workDate}</span>}
              </div>
              <div className="ts-form-group ts-project-picker-wrap" ref={projectPickerRef}>
                <label>Project</label>
                <div
                  className="ts-project-picker-trigger"
                  onClick={() => setProjectDropdownOpen(prev => !prev)}
                  tabIndex={0}
                >
                  <span className={selectedProject ? 'ts-project-chosen' : 'ts-project-placeholder'}>
                    {selectedProject ? selectedProject.name : 'Select a Project (or No Project)'}
                  </span>
                  <span className="ts-project-arrow">{projectDropdownOpen ? '▲' : '▼'}</span>
                </div>

                {projectDropdownOpen && (
                  <div className="ts-project-dropdown">
                    <div className="ts-project-search-box">
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProjectSearch(val);
                          fetchProjects(0, val);
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      {projectSearch && (
                        <button
                          type="button"
                          className="ts-project-clear-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjectSearch('');
                            fetchProjects(0, '');
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </div>

                    <div className="ts-project-list">
                      <div
                        className={`ts-project-option ${!form.projectId ? 'is-selected' : ''}`}
                        onClick={() => {
                          setForm(prev => ({ ...prev, projectId: '' }));
                          setSelectedProject(null);
                          setProjectDropdownOpen(false);
                        }}
                      >
                        <span className="ts-project-option-name"><em>No Project</em></span>
                        {!form.projectId && <span className="ts-project-check">✓</span>}
                      </div>

                      {projectLoading ? (
                        <div className="ts-project-loading">Loading projects...</div>
                      ) : projects.length === 0 ? (
                        <div className="ts-project-empty">No projects found</div>
                      ) : (
                        projects.map(p => {
                          const isSelected = String(form.projectId) === String(p.id);
                          return (
                            <div
                              key={p.id}
                              className={`ts-project-option ${isSelected ? 'is-selected' : ''}`}
                              onClick={() => {
                                setForm(prev => ({ ...prev, projectId: p.id }));
                                setSelectedProject(p);
                                setProjectDropdownOpen(false);
                              }}
                            >
                              <div className="ts-project-option-info">
                                <span className="ts-project-option-name">{p.name}</span>
                                {p.customerName && (
                                  <span className="ts-project-option-sub">{p.customerName}</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {p.status && (
                                  <span className={`ts-project-status-tag status-${p.status.toLowerCase()}`}>
                                    {p.status}
                                  </span>
                                )}
                                {isSelected && <span className="ts-project-check">✓</span>}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="ts-project-pagination" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="ts-page-btn"
                        disabled={projectPage <= 0 || projectLoading}
                        onClick={() => fetchProjects(projectPage - 1, projectSearch)}
                      >
                        ◀ Prev
                      </button>
                      <span className="ts-page-info">
                        Page {projectTotalPages > 0 ? projectPage + 1 : 0} of {projectTotalPages}
                        {projectTotalElements > 0 && ` (${projectTotalElements})`}
                      </span>
                      <button
                        type="button"
                        className="ts-page-btn"
                        disabled={projectPage >= projectTotalPages - 1 || projectLoading}
                        onClick={() => fetchProjects(projectPage + 1, projectSearch)}
                      >
                        Next ▶
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="ts-form-row">
              <div className="ts-form-group">
                <label>Start Time</label>
                <input type="time" name="startTime" value={form.startTime} onChange={handleChange} />
              </div>
              <div className="ts-form-group">
                <label>End Time</label>
                <input type="time" name="endTime" value={form.endTime} onChange={handleChange}
                  className={formErrors.endTime ? 'error' : ''} />
                {formErrors.endTime && <span className="ts-error">{formErrors.endTime}</span>}
              </div>
            </div>
            <div className="ts-form-group">
              <label>Hours Worked *</label>
              <div className="ts-hours-row">
                <input type="number" name="hoursWorked" value={form.hoursWorked} onChange={handleChange}
                  placeholder="e.g. 8" step="0.01" min="0.01" max="24"
                  className={formErrors.hoursWorked ? 'error' : ''} required />
                {form.startTime && form.endTime && <span className="ts-auto-badge">auto-calculated</span>}
              </div>
              {formErrors.hoursWorked && <span className="ts-error">{formErrors.hoursWorked}</span>}
            </div>
            <div className="ts-form-group">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange}
                rows={3} placeholder="What did you work on?" />
            </div>
            <div className="ts-form-actions">
              <button type="button" className="ts-btn-secondary" onClick={closeForm}>Cancel</button>
              <button type="submit" className="ts-btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : (editingId ? 'Update' : 'Submit')}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="ts-loading">Loading…</div>
      ) : timesheets.length === 0 ? (
        <div className="ts-empty"><div className="ts-empty-icon">⏱</div><p>No timesheets yet.</p></div>
      ) : (
        <div className="ts-table-wrap">
          <table className="ts-table">
            <thead>
              <tr>
                <th>Date</th><th>Start</th><th>End</th><th>Hours</th>
                <th>Project</th><th>Description</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map(t => (
                <tr key={t.id} className="ts-clickable-row" onClick={() => setSelected(t)}>
                  <td data-label="Date"><strong>{fmt(t.workDate)}</strong></td>
                  <td data-label="Start">{t.startTime ? t.startTime.slice(0, 5) : <span className="ts-muted">—</span>}</td>
                  <td data-label="End">{t.endTime   ? t.endTime.slice(0, 5)   : <span className="ts-muted">—</span>}</td>
                  <td data-label="Hours"><span className="ts-hours">{t.hoursWorked}h</span></td>
                  <td data-label="Project">{t.projectName || <span className="ts-muted">—</span>}</td>
                  <td data-label="Description" className="ts-desc-preview">
                    {t.description
                      ? (t.description.length > 50 ? t.description.slice(0, 50) + '…' : t.description)
                      : <span className="ts-muted">—</span>}
                  </td>
                  <td data-label="Status">
                    <span className={`ts-badge ${t.approvedByName ? 'ts-badge-approved' : 'ts-badge-pending'}`}>
                      {t.approvedByName ? `✓ ${t.approvedByName}` : 'Pending Approval'}
                    </span>
                  </td>
                  <td data-label="Actions" onClick={e => e.stopPropagation()}>
                    <div className="action-buttons ts-action-btns">
                      <button className="btn btn-sm btn-primary ts-view-btn" onClick={() => setSelected(t)}>View</button>
                      {!t.approvedByName && (
                        <button className="btn btn-sm btn-secondary ts-edit-btn" onClick={() => openEdit(t)} title="Edit">✏️ Edit</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ViewModal timesheet={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default MyTimesheets;
